import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { Seo } from "@/shared/components/Seo";
import { LandingPathContext } from "./Cta";
import { LandingLayout } from "./LandingLayout";
import { buildLandingJsonLd } from "./jsonLd";
import { Hero, Section } from "./Sections";
import { IS_PRERENDER } from "./prerender";
import { findDomScrollHost, LandingScrollHostContext, scrollHostToTop, } from "./scrollHost";
import { isStaticFirstRender, usePageReady } from "@/shared/lib/prerender";
import styles from "./Landing.module.scss";
/**
 * Страница-лендинг целиком из конфига: Seo + лейаут + hero + секции.
 * Тема — CSS-переменные на корне (--l-accent/--l-accent-2/…) и `data-mood`
 * для декора; светлая/тёмная — глобальные токены --vp-* ([data-theme] на <html>).
 */
export const LandingPage = ({ config }) => {
    const { theme } = config;
    const jsonLd = React.useMemo(() => buildLandingJsonLd(config), [config]);
    const anchors = config.sections.map((s) => s.id ?? (s.type === "faq" ? "faq" : "")).filter(Boolean);
    const primaryCta = config.hero.ctas.find((c) => !c.anchor)?.label ?? "Создать песню";
    // Страница, открытая поверх своего снимка пререндера: человек уже видит её
    // статику и мог прокрутить. Для неё — без анимации появления hero (иначе при
    // подмене видимый текст «въехал» бы второй раз) и без прокрутки наверх.
    // Только для ЭТОГО пути: переход на другой лендинг — обычная навигация.
    const [staticPath] = React.useState(() => (isStaticFirstRender() ? config.path : null));
    const overSnapshot = staticPath === config.path;
    usePageReady();
    // Кто скроллится под страницей: документ (лендинг вне app-shell) или
    // контейнер app-shell (главная «/» внутри реального TG/MAX-мини-аппа и пока
    // идёт проверка сессии). Ищем один раз после монтирования — см. scrollHost.ts.
    const pageRef = React.useRef(null);
    const [scrollHost, setScrollHost] = React.useState(null);
    React.useLayoutEffect(() => {
        setScrollHost(findDomScrollHost(pageRef.current));
    }, []);
    // При переходе между лендингами — наверх (того скроллера, который реальный).
    React.useEffect(() => {
        if (overSnapshot)
            return;
        scrollHostToTop(scrollHost);
    }, [config.path, overSnapshot, scrollHost]);
    const style = {
        "--l-accent": theme.accent,
        "--l-accent-2": theme.accent2,
        "--l-on-accent": theme.onAccent ?? "#fff",
    };
    return (_jsx(LandingPathContext.Provider, { value: config.path, children: _jsx(LandingScrollHostContext.Provider, { value: scrollHost, children: _jsxs("div", { ref: pageRef, className: styles.page, style: style, "data-mood": theme.mood, "data-static": IS_PRERENDER || undefined, "data-noenter": (overSnapshot && !IS_PRERENDER) || undefined, children: [_jsx(Seo, { title: config.seo.title, description: config.seo.description, path: config.path, jsonLd: jsonLd }), _jsxs(LandingLayout, { path: config.path, anchors: anchors, ctaLabel: primaryCta, children: [_jsx(Hero, { hero: config.hero, mood: theme.mood }), config.sections.map((s, i) => (_jsx(Section, { section: s, index: i, mood: theme.mood }, `${s.type}-${i}`)))] })] }) }) }));
};
